/** App shell — icon rail, top bar, sync pill, command palette.
 *
 * MASTER_PROMPT.md section 7.9.
 */

import { useEffect, useMemo, useState } from "react";
import { syncPeriodToUrl } from "../../lib/reportPeriod";
import type { User } from "../../lib/types";
import { api, STOCK_WARNING_EVENT, syncQueue } from "../../lib/api";
import { onQueueChange } from "../../lib/offline";
import { auth } from "../../lib/auth";
import { ErrorBoundary } from "./ErrorBoundary";
import { CommandPalette } from "./CommandPalette";
import { ShiftEntry } from "../production/ShiftEntry";
import { VarianceQueue } from "../variance/VarianceQueue";
import { StockView } from "../stock/StockView";
import { PurchaseEntry } from "../purchase/PurchaseEntry";
import { LoomFloor } from "../looms/LoomFloor";
import { Executive } from "../dashboard/Executive";
import { DispatchDesk } from "../dispatch/DispatchDesk";
import { Transfers } from "../transfer/Transfers";
import { TallyDesk } from "../tally/TallyDesk";
import { CostingDesk } from "../costing/CostingDesk";
import { StockCounts } from "../counts/StockCounts";
import { IntakeDesk } from "../intake/IntakeDesk";
import { PeriodClose } from "../close/PeriodClose";
import { WipLadder } from "../wip/WipLadder";
import { Recycling } from "../dashboard/Recycling";
import { Ink } from "../dashboard/Ink";
import { DispatchBoard } from "../dashboard/DispatchBoard";
import { MfaEnrolment } from "../auth/MfaEnrolment";
import { MyAccount } from "../auth/MyAccount";
import { Settings } from "../settings/Settings";
import { Entries } from "../entries/Entries";
import { ProductionReport } from "../reports/ProductionReport";
import { ExceptionsReport } from "../reports/ExceptionsReport";
import { MachineEfficiency } from "../reports/MachineEfficiency";
import { DrillProvider } from "../../components/Drill";
import type { Blocked } from "../../lib/types";
import { fmtQty } from "../../lib/format";

export type Route =
  | "executive"
  | "entry"
  | "looms"
  | "entries"
  | "production"
  | "exceptions"
  | "efficiency"
  | "variance"
  | "purchase"
  | "stock"
  | "dispatch"
  | "transfers"
  | "counts"
  | "tally"
  | "costing"
  | "intake"
  | "close"
  | "wip"
  | "recycling"
  | "ink"
  | "dispatchboard"
  | "settings"
  | "account";

/**
 * The rail, and the module each screen needs VIEW on.
 *
 * `module` is not decoration: it is the same permission the corresponding API
 * route gates on, so the rail shows a door only where the key exists. The
 * server is still the authority — every one of these calls is checked again —
 * but a weighbridge operator being shown Costing and Period Close, then
 * getting a 403 for clicking them, is the rail lying about what the account
 * can do.
 *
 * `superAdmin` is the one thing the module matrix cannot express. An auditor
 * holds VIEW on every module including users, which is right — they read
 * everything — but Settings is not a reading screen, and it is gated on the
 * role by the server for the same reason.
 */
export const NAV: {
  route: Route;
  label: string;
  glyph: string;
  group: string;
  module: string;
  superAdmin?: boolean;
}[] = [
  { route: "executive", label: "Control tower", glyph: "◈", group: "Overview", module: "reports" },
  { route: "entry", label: "Shift entry", glyph: "▤", group: "Operations", module: "production" },
  { route: "looms", label: "Loom floor", glyph: "▦", group: "Operations", module: "production" },
  { route: "entries", label: "Entries", glyph: "≡", group: "Operations", module: "production" },
  {
    route: "production",
    label: "Production report",
    glyph: "Σ",
    group: "Overview",
    module: "reports",
  },
  {
    route: "efficiency",
    label: "Machine efficiency",
    glyph: "⚙",
    group: "Overview",
    module: "reports",
  },
  {
    route: "exceptions",
    label: "Exceptions",
    glyph: "⚑",
    group: "Overview",
    module: "audit",
  },
  {
    route: "variance",
    label: "Variance queue",
    glyph: "△",
    group: "Operations",
    module: "variance",
  },
  { route: "purchase", label: "Goods receipt", glyph: "↓", group: "Inventory", module: "purchase" },
  {
    route: "recycling",
    label: "Recycling line",
    glyph: "♺",
    group: "Overview",
    module: "reports",
  },
  { route: "ink", label: "Printing & ink", glyph: "◐", group: "Overview", module: "reports" },
  {
    route: "dispatchboard",
    label: "Dispatch board",
    glyph: "⇥",
    group: "Overview",
    module: "reports",
  },
  { route: "stock", label: "Stock", glyph: "◫", group: "Inventory", module: "stock" },
  { route: "wip", label: "WIP ladder", glyph: "☰", group: "Inventory", module: "stock" },
  { route: "dispatch", label: "Dispatch", glyph: "↗", group: "Inventory", module: "dispatch" },
  { route: "transfers", label: "Transfers", glyph: "⇄", group: "Inventory", module: "transfer" },
  { route: "counts", label: "Stock counts", glyph: "☑", group: "Inventory", module: "stock" },
  { route: "tally", label: "Tally", glyph: "₹", group: "Finance", module: "tally" },
  { route: "costing", label: "Costing", glyph: "∑", group: "Finance", module: "costing" },
  { route: "intake", label: "Data intake", glyph: "⇧", group: "Finance", module: "masters" },
  { route: "close", label: "Period close", glyph: "🔒", group: "Finance", module: "reports" },
  {
    route: "settings",
    label: "Settings",
    glyph: "⚙",
    group: "Administration",
    module: "users",
    superAdmin: true,
  },
];

/** Routes reachable from somewhere other than the rail. Every role has them,
 *  so they are never filtered by the permitted-module list. */
const OFF_RAIL = new Set<Route>(["account"]);

interface Props {
  user: User;
  modules: string[];
  onSignOut: () => void;
  /** A rename has to reach the top bar, or the person sees the old one. */
  onUserChange: (user: User) => void;
}

export function AppShell({ user, modules, onSignOut, onUserChange }: Props) {
  // The rail, scoped to what this account may open. An empty list means the
  // caller could not tell us — fall back to showing everything rather than
  // stranding somebody on a blank shell; the API refuses what it must.
  const nav = useMemo(() => {
    const permitted = modules.length
      ? NAV.filter((n) => modules.includes(n.module))
      : NAV;
    return permitted.filter(
      (n) => !n.superAdmin || user.role === "SUPER_ADMIN"
    );
  }, [modules, user.role]);

  // A link can name the screen (`?view=recycling&from=…&to=…`), so a report
  // sent to somebody opens on the same report and the same period. Only a
  // screen this account may open is honoured.
  const [route, setRoute] = useState<Route>(() => {
    const asked = new URLSearchParams(window.location.search).get("view");
    const linked = nav.find((n) => n.route === asked)?.route;
    return linked ?? nav.find((n) => n.route === "executive")?.route ?? nav[0]?.route ?? "executive";
  });

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("view", route);
      window.history.replaceState(window.history.state, "",
        `${window.location.pathname}?${params.toString()}${window.location.hash}`);
      syncPeriodToUrl();
    } catch {
      // The address bar is a convenience; the screen works without it.
    }
  }, [route]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [version, setVersion] = useState<string | null>(null);

  // If the permitted set changes under us, never leave the user parked on a
  // screen that is no longer in their rail.
  //
  // `OFF_RAIL` is exempt. Those screens are reached from the top bar rather
  // than from the rail, so "not in the rail" is their normal state — without
  // this the guard fired the instant one opened and bounced straight back to
  // the first rail item, which looked exactly like a dead button.
  useEffect(() => {
    if (OFF_RAIL.has(route)) return;
    if (nav.length && !nav.some((n) => n.route === route)) {
      setRoute(nav[0].route);
    }
  }, [nav, route]);

  useEffect(() => {
    api.heartbeat().then((h) => setVersion(h.version)).catch(() => {});
  }, []);
  const [railOpen, setRailOpen] = useState(false);
  // The rail is two different controls wearing one class name: a hover-
  // expanding icon strip where there is a pointer, and a drawer where there is
  // not. Touch never fires mouseenter, so below the breakpoint the hover
  // handlers come off entirely and the topbar button drives it instead —
  // otherwise a tap on a nav item would latch the rail open over the screen it
  // just navigated to. Matched in JS, not only CSS, because it decides which
  // handlers exist, not just how things look.
  const [compact, setCompact] = useState(
    () => window.matchMedia("(max-width: 720px)").matches
  );
  const [queued, setQueued] = useState(0);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [enrolPrompt, setEnrolPrompt] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  // Negative stock is allowed but never silent: whichever screen posted it,
  // the shortfall is shown here until dismissed.
  const [stockWarnings, setStockWarnings] = useState<Blocked[]>([]);
  // An entry opened for editing from the Entries list.
  const [editing, setEditing] = useState<{ id: number; kind: "SHIFT" | "LOOM" } | null>(null);

  useEffect(() => {
    const onWarn = (e: Event) =>
      setStockWarnings((e as CustomEvent<Blocked[]>).detail);
    window.addEventListener(STOCK_WARNING_EVENT, onWarn);
    return () => window.removeEventListener(STOCK_WARNING_EVENT, onWarn);
  }, []);

  function openEdit(id: number, kind: "SHIFT" | "LOOM") {
    setEditing({ id, kind });
    setRoute(kind === "LOOM" ? "looms" : "entry");
  }

  function closeEdit() {
    setEditing(null);
    setRoute("entries");
  }

  // Approvers who have not enrolled are prompted on arrival. They are not
  // locked out — that would be circular — but the gap is not left silent.
  useEffect(() => {
    auth
      .mfaStatus()
      .then((s) => setEnrolPrompt(s.required && !s.enrolled))
      .catch(() => undefined);
  }, []);

  // Crossing the breakpoint mid-session swaps the rail between its two modes,
  // so drop it shut rather than carrying a hover state into a drawer.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const onChange = (e: MediaQueryListEvent) => {
      setCompact(e.matches);
      setRailOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => onQueueChange(setQueued), []);

  // Drain the offline buffer: once on arrival, and again the moment the
  // browser says the connection is back. Without this the queue only ever
  // filled — the pill counted up and never down.
  useEffect(() => {
    const drain = () => {
      if (!navigator.onLine) return;
      void syncQueue()
        .then((r) => {
          // No `alive` guard: a StrictMode remount would discard the outcome
          // of the run that actually sent the writes, and setting state on an
          // unmounted component is a no-op in React 18 anyway.
          if (!r.sent && !r.failed) return;
          setSyncNote(
            r.failed
              ? `${r.sent} write(s) synced. ${r.failed} were refused by the ` +
                `server and could not be saved — they have been discarded: ` +
                r.rejected.map((x) => x.path).join(", ")
              : `${r.sent} write(s) synced.`
          );
        })
        .catch(() => undefined);
    };

    drain();
    window.addEventListener("online", drain);
    return () => window.removeEventListener("online", drain);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // Command palette — the single most "AI tool" affordance (section 7.4).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
      if (e.key === "Escape") {
        setPaletteOpen(false);
        setRailOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <DrillProvider>
    <div className="shell">
      {/* The scrim is the drawer's other half: it dims the screen the drawer is
          covering and gives a tap target for dismissing it, which a drawer with
          no close affordance otherwise lacks. Only ever rendered compact. */}
      {compact && railOpen && (
        <div
          className="rail__scrim"
          onClick={() => setRailOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav
        id="rail-nav"
        className={`rail${railOpen ? " rail--open" : ""}`}
        onMouseEnter={compact ? undefined : () => setRailOpen(true)}
        onMouseLeave={compact ? undefined : () => setRailOpen(false)}
        aria-label="Main"
      >
        <div className="rail__brand">
          <span className="rail__diamond" />
          <span className="rail__wordmark">NEG</span>
        </div>

        {/* The groups scroll on their own so a full rail — seventeen entries for
            a super admin — cannot push Period close off the bottom of a laptop
            screen. The brand above and the build below stay put. */}
        <div className="rail__nav">
          {["Overview", "Operations", "Inventory", "Finance", "Administration"]
            .filter((group) => nav.some((n) => n.group === group))
            .map((group) => (
              <div key={group} className="rail__group">
                <span className="rail__group-label">{group}</span>
                {nav
                  .filter((n) => n.group === group)
                  .map((item) => (
                    <button
                      key={item.route}
                      className={`rail__item${
                        route === item.route ? " rail__item--active" : ""
                      }`}
                      onClick={() => {
                        setEditing(null);
                        setRoute(item.route);
                        // Only compact: on a pointer the rail is still under
                        // the cursor, and collapsing it here would leave it
                        // shut until the mouse left and came back.
                        if (compact) setRailOpen(false);
                      }}
                      aria-current={route === item.route ? "page" : undefined}
                      title={item.label}
                    >
                      <span className="rail__glyph" aria-hidden="true">
                        {item.glyph}
                      </span>
                      <span className="rail__label">{item.label}</span>
                    </button>
                  ))}
              </div>
            ))}
        </div>
        {/* The build, at the foot of the rail. Same source as the login
            screen: what the server is running, not what this bundle was
            compiled from — those diverge exactly when it matters. */}
        <div className="rail__version" title="Application version">
          v{version ?? "—"}
        </div>
      </nav>

      <div className="shell__main">
        <header className="topbar">
          <div className="topbar__left">
            <button
              className="topbar__menu"
              onClick={() => setRailOpen((open) => !open)}
              aria-label={railOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={railOpen}
              aria-controls="rail-nav"
            >
              ☰
            </button>
            <span className="topbar__unit">NEG Plant</span>
            <span className="topbar__crumb">
              {nav.find((n) => n.route === route)?.label}
            </span>
          </div>

          <div className="topbar__right">
            {queued > 0 && (
              <span className="pill pill--warning" title="Waiting to sync">
                ◷ {queued} pending sync
              </span>
            )}
            <button
              className="topbar__btn"
              onClick={() => setPaletteOpen(true)}
              aria-label="Open command palette"
            >
              Search <kbd>⌘K</kbd>
            </button>
            <button
              className="topbar__icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            >
              {theme === "dark" ? "☾" : "☀"}
            </button>
            <div className="topbar__user">
              {/* Your own name is the way into your own account. It is not a
                  rail item because it is about you rather than about the
                  plant, and every role has one. */}
              <button
                className={`topbar__identity topbar__identity--button${
                  route === "account" ? " topbar__identity--active" : ""
                }`}
                onClick={() => setRoute("account")}
                aria-current={route === "account" ? "page" : undefined}
                title="Your name and password"
              >
                <span className="topbar__avatar" aria-hidden="true">
                  {user.name.charAt(0)}
                </span>
                <span className="topbar__identity-text">
                  <span className="topbar__name">{user.name}</span>
                  <span className="topbar__role">
                    {user.role.replace(/_/g, " ").toLowerCase()}
                  </span>
                </span>
              </button>
              <button className="topbar__btn" onClick={onSignOut}>
                Sign out
              </button>
            </div>
          </div>
        </header>

        <main className="content">
          {stockWarnings.length > 0 && (
            <div className="banner banner--warning" role="alert">
              <div className="stockwarn">
                <strong>
                  Saved, but stock is now below zero
                  {stockWarnings.length > 1 ? ` for ${stockWarnings.length} materials` : ""}.
                </strong>
                <ul className="banner__list">
                  {stockWarnings.map((w) => (
                    <li key={`${w.item}-${w.location}`}>
                      {w.item} at {w.location}: {fmtQty(w.available, 2)} was
                      available, {fmtQty(w.requested, 2)} taken — short by{" "}
                      {fmtQty(w.short_by, 2)}.
                    </li>
                  ))}
                </ul>
                <span className="page__note">
                  This goes on the Exceptions report. A missing receipt or a
                  mistyped quantity is the usual cause.
                </span>
              </div>
              <button className="btn btn--ghost btn--auto"
                      onClick={() => setStockWarnings([])}>
                Dismiss
              </button>
            </div>
          )}
          {syncNote && (
            <div className="banner banner--warning" role="status">
              {syncNote}
              <button
                className="btn btn--ghost btn--auto"
                onClick={() => setSyncNote(null)}
              >
                Dismiss
              </button>
            </div>
          )}
          {enrolPrompt && (
            <MfaEnrolment
              onDone={() => setEnrolPrompt(false)}
              onCancel={() => setEnrolPrompt(false)}
            />
          )}
          <ErrorBoundary resetKey={`${route}-${editing?.id ?? ""}`}>
            {route === "executive" && <Executive />}
            {route === "entry" && (
              <ShiftEntry
                key={editing?.id ?? "new"}
                user={user}
                editId={editing?.kind === "SHIFT" ? editing.id : null}
                onDone={closeEdit}
              />
            )}
            {route === "looms" && (
              <LoomFloor
                key={editing?.id ?? "new"}
                user={user}
                editId={editing?.kind === "LOOM" ? editing.id : null}
                onDone={closeEdit}
              />
            )}
            {route === "entries" && <Entries user={user} onEdit={openEdit} />}
            {route === "production" && <ProductionReport />}
            {route === "exceptions" && <ExceptionsReport />}
            {route === "efficiency" && <MachineEfficiency />}
            {route === "variance" && <VarianceQueue user={user} />}
            {route === "stock" && <StockView />}
            {route === "purchase" && <PurchaseEntry />}
            {route === "dispatch" && <DispatchDesk />}
            {route === "transfers" && <Transfers />}
            {route === "counts" && <StockCounts />}
            {route === "tally" && <TallyDesk />}
            {route === "costing" && <CostingDesk />}
            {route === "intake" && <IntakeDesk user={user} />}
            {route === "close" && <PeriodClose user={user} />}
            {route === "wip" && <WipLadder />}
            {route === "recycling" && <Recycling />}
            {route === "ink" && <Ink />}
            {route === "dispatchboard" && <DispatchBoard />}
            {route === "settings" && <Settings user={user} />}
            {route === "account" && (
              <MyAccount user={user} onUserChange={onUserChange} />
            )}
          </ErrorBoundary>
        </main>
      </div>

      {paletteOpen && (
        <CommandPalette
          routes={nav.map((n) => n.route)}
          onClose={() => setPaletteOpen(false)}
          onNavigate={(r) => {
            setRoute(r);
            setPaletteOpen(false);
            if (compact) setRailOpen(false);
          }}
        />
      )}
    </div>
    </DrillProvider>
  );
}
